import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  auth,
  setUnauthorizedHandler,
  type SignupBody,
} from '../api/client';
import { purgeForeignDrafts } from '../lib/draftStore';
import type { MeResponse, Membership, User } from '../api/types';

export interface AuthContextValue {
  user: User | null;
  memberships: Membership[];
  /** The membership for the currently selected org (cookie state). */
  currentOrg: Membership | null;
  /** True when the current workspace is a personal (Individual) workspace. */
  isIndividual: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<MeResponse>;
  signup: (body: SignupBody) => Promise<MeResponse>;
  logout: () => Promise<void>;
  selectOrg: (organizationId: string) => Promise<void>;
  /**
   * Create an additional organization workspace (Enterprise: up to two).
   * Refreshes memberships and switches to the new workspace. Resolves with
   * the new organization's id.
   */
  createOrg: (name: string) => Promise<string>;
  refresh: () => Promise<void>;
  /**
   * True right after a fresh login or signup in this tab (password login,
   * signup, or a completed Google OAuth flow). The profile-nudge modal
   * consumes it: it shows while the profile is under 80% complete, and the
   * user dismisses it per session.
   */
  profileNudge: boolean;
  dismissProfileNudge: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileNudge, setProfileNudge] = useState(false);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const applyMe = useCallback((me: {
    user: User;
    memberships: Membership[];
    currentOrgId: string | null;
  }) => {
    setUser(me.user);
    setMemberships(me.memberships);
    setCurrentOrgId(me.currentOrgId);
    // Wave 3 (D12 family): draft mirrors are scoped to user + org.
    // Whenever the session identity settles (login, signup, refresh,
    // org switch), sweep away every draft that is not the CURRENT
    // identity's — a previous user's or workspace's draft must never
    // be loadable, restorable, or autosavable from this browser again.
    if (me.currentOrgId) {
      purgeForeignDrafts({ userId: me.user.id, orgId: me.currentOrgId, beneficiaryId: null });
    } else {
      purgeForeignDrafts(null);
    }
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    setMemberships([]);
    setCurrentOrgId(null);
    setProfileNudge(false);
    // Logout and session expiry (the 401 handler funnels here): no
    // draft survives — the next person at this browser starts clean.
    purgeForeignDrafts(null);
  }, []);

  // Global 401 handler: any api() call that returns 401 clears the session.
  // Only bounce to /login from protected routes — public pages (/, /pricing,
  // /login, /signup) must never redirect away.
  //
  // R2-BR-D34: the bounce MUST carry the current destination as router
  // state, exactly like RequireAuth's own <Navigate state={{from}}>.
  // Pre-fix this handler navigated bare — and because the boot-time
  // /auth/me 401 fires through here BEFORE RequireAuth can render its
  // state-carrying Navigate, the deep link was lost at this hop and
  // every signed-out deep link landed on Overview after sign-in.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession();
      if (window.location.pathname.startsWith('/app')) {
        navigateRef.current('/login', {
          replace: true,
          state: {
            from: { pathname: window.location.pathname, search: window.location.search },
          },
        });
      }
    });
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  const refresh = useCallback(async () => {
    try {
      const me = await auth.me();
      applyMe(me);
      // A completed Google OAuth flow reloads the app, so boot-time session
      // restoration is the only place that can see it. The sign-in button
      // leaves a same-tab marker; honor it as a fresh login for the
      // profile-nudge check, then clear it either way.
      try {
        if (sessionStorage.getItem('grantnav.oauthFreshLogin') === '1') {
          setProfileNudge(true);
        }
        sessionStorage.removeItem('grantnav.oauthFreshLogin');
      } catch {
        /* storage unavailable — skip the nudge */
      }
    } catch {
      clearSession();
    } finally {
      setLoading(false);
    }
  }, [applyMe, clearSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await auth.login(email, password);
      applyMe(res);
      setProfileNudge(true);
      return res;
    },
    [applyMe],
  );

  const signup = useCallback(
    async (body: SignupBody) => {
      await auth.signup(body);
      // Signup sets the session cookie with the org selected; reload session state.
      const me = await auth.me();
      applyMe(me);
      setProfileNudge(true);
      return me;
    },
    [applyMe],
  );

  const dismissProfileNudge = useCallback(() => setProfileNudge(false), []);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      clearSession();
      navigateRef.current('/login', { replace: true });
    }
  }, [clearSession]);

  const selectOrg = useCallback(
    async (organizationId: string) => {
      const res = await auth.selectOrg(organizationId);
      setCurrentOrgId(res.currentOrgId);
      const me = await auth.me();
      applyMe(me);
    },
    [applyMe],
  );

  const currentOrg = useMemo(
    () => memberships.find((m) => m.organizationId === currentOrgId) ?? null,
    [memberships, currentOrgId],
  );

  const createOrg = useCallback(
    async (name: string) => {
      const res = await auth.createOrg(name);
      // selectOrg re-reads /auth/me, so the new workspace is in memberships.
      await selectOrg(res.organization.id);
      return res.organization.id;
    },
    [selectOrg],
  );

  const isIndividual = currentOrg?.kind === 'INDIVIDUAL';

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      memberships,
      currentOrg,
      isIndividual,
      loading,
      login,
      signup,
      logout,
      selectOrg,
      createOrg,
      refresh,
      profileNudge,
      dismissProfileNudge,
    }),
    [user, memberships, currentOrg, isIndividual, loading, login, signup, logout, selectOrg, createOrg, refresh, profileNudge, dismissProfileNudge],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
